import React from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight } from "lucide-react";
import { trackCalcEntry } from "../utils/analytics";

/**
 * CalculatorCallout — a tasteful inline block linking to the claim
 * calculator. Same visual shell on every page, but the copy is
 * passed in per-page so it reads as part of the surrounding content
 * (not boilerplate).
 *
 * Props:
 *  - eyebrow   : small uppercase label on top (e.g. "60-Second Estimate")
 *  - headline  : the main attention-grab (1 short sentence)
 *  - body      : the contextual paragraph that fits the page's voice
 *  - ctaLabel  : button text (defaults to "Try The Calculator")
 *  - compact   : when true, renders a smaller inline-link variant
 *                (use this inside blog posts mid-article)
 */
const CalculatorCallout = ({
  eyebrow = "60-Second Claim Estimate",
  headline,
  body,
  ctaLabel = "Try The Calculator",
  compact = false,
  source = "calc_callout",
}) => {
  const handleClick = () =>
    trackCalcEntry("cta_click", source, { cta_type: "calc_callout" });
  if (compact) {
    return (
      <div className="my-8 p-5 bg-[#E11D26]/10 border-l-4 border-[#E11D26]">
        <p className="text-white text-base leading-relaxed">
          <Calculator className="inline h-5 w-5 mr-2 text-[#E11D26] -mt-1" />
          {body}{" "}
          <Link
            to="/claim-calculator"
            onClick={handleClick}
            className="text-[#E11D26] hover:text-white underline underline-offset-2 font-bold whitespace-nowrap"
            data-testid="calc-callout-compact-link"
          >
            {ctaLabel} →
          </Link>
        </p>
      </div>
    );
  }

  return (
    <section className="py-12 md:py-20 px-4 sm:px-6" data-testid="calc-callout-section">
      <div className="max-w-6xl mx-auto">
        <div
          className="relative overflow-hidden rounded-2xl border border-[#E11D26]/40 shadow-[0_25px_70px_-15px_rgba(225,29,38,0.55)]"
          style={{
            background:
              "linear-gradient(135deg, #E11D26 0%, #B0151C 45%, #1A0507 100%)",
          }}
        >
          {/* Diagonal stripe pattern overlay */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, #fff 0 2px, transparent 2px 18px)",
            }}
          />
          {/* Soft red glow on the right */}
          <div
            aria-hidden="true"
            className="absolute -right-24 -top-24 w-80 h-80 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{ background: "#FF3B3B" }}
          />

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-8 px-6 sm:px-10 md:px-12 py-10 md:py-12">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm border border-white/15 text-white text-[11px] font-bold tracking-[0.25em] uppercase">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                <Calculator className="h-3.5 w-3.5" />
                {eyebrow}
              </div>
              {headline && (
                <h2
                  className="mt-4 text-3xl sm:text-4xl md:text-5xl font-black text-white uppercase leading-[1.05] drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {headline}
                </h2>
              )}
              {body && (
                <p className="mt-4 text-white/90 leading-relaxed max-w-2xl text-base sm:text-lg">
                  {body}
                </p>
              )}
            </div>
            <Link
              to="/claim-calculator"
              onClick={handleClick}
              className="group shrink-0 inline-flex items-center gap-3 bg-white hover:bg-black text-[#E11D26] hover:text-white px-8 py-5 font-black tracking-wider uppercase text-sm sm:text-base rounded-xl shadow-2xl ring-1 ring-white/30 hover:ring-white transition-all duration-200 hover:-translate-y-0.5"
              data-testid="calc-callout-link"
            >
              {ctaLabel}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalculatorCallout;
